/**
 * The privacy vocabulary shared by API, web and docs: how SARTHI's fields are
 * classified, and which purposes personal data is processed for.
 *
 * Machine-readable on purpose. docs/privacy/DATA-INVENTORY.md is generated
 * from the same map in review, a test fails when a sensitive field is added
 * to the schema without a classification, and the masking and export code
 * read it rather than keeping lists of their own.
 */
export const DATA_CLASSES = [
    'PUBLIC',
    'INTERNAL',
    'PERSONAL',
    'SENSITIVE_PERSONAL',
    'CREDENTIAL',
    'FINANCIAL',
    'GOVERNMENT_IDENTIFIER',
    'AUTHENTICATION_SECRET',
];
/**
 * `Model.field` → class, for every field that is more than INTERNAL.
 *
 * Deliberately not over-classified: an operational field that only names a
 * business (a supplier's company name, a property's address) is PERSONAL only
 * where it can identify an individual - a proprietor, a guest. Anything not
 * listed is INTERNAL.
 */
export const DATA_CLASSIFICATION = {
    // Guests and the people behind bookings, reservations and orders.
    'Guest.name': 'PERSONAL',
    'Guest.phone': 'PERSONAL',
    'Guest.email': 'PERSONAL',
    'Guest.address': 'PERSONAL',
    'Guest.gstin': 'PERSONAL',
    'Guest.idType': 'PERSONAL',
    'Guest.idNumber': 'GOVERNMENT_IDENTIFIER',
    'Guest.idNumberLast4': 'PERSONAL',
    'Stay.children': 'PERSONAL',
    'RoomBooking.guestName': 'PERSONAL',
    'RoomBooking.guestPhone': 'PERSONAL',
    'RoomBooking.guestEmail': 'PERSONAL',
    'RoomBooking.note': 'PERSONAL',
    'Reservation.guestName': 'PERSONAL',
    'Reservation.phone': 'PERSONAL',
    'Reservation.email': 'PERSONAL',
    'Reservation.note': 'PERSONAL',
    'Order.customerName': 'PERSONAL',
    'Order.customerPhone': 'PERSONAL',
    'Order.deliveryAddress': 'PERSONAL',
    // Free text a guest dictates - may carry dietary or health information.
    'OrderLine.note': 'SENSITIVE_PERSONAL',
    'Invoice.guestName': 'PERSONAL',
    'Invoice.guestGstin': 'PERSONAL',
    'Payment.payerName': 'PERSONAL',
    'Payment.payerEmail': 'PERSONAL',
    // Staff.
    'Employee.name': 'PERSONAL',
    'Employee.phone': 'PERSONAL',
    'Employee.bankAccount': 'FINANCIAL',
    'Employee.bankAccountLast4': 'PERSONAL',
    'Employee.ifsc': 'FINANCIAL',
    'Employee.bankName': 'FINANCIAL',
    'Employee.monthlyGross': 'FINANCIAL',
    'Employee.dailyWage': 'FINANCIAL',
    'Employee.advanceBalance': 'FINANCIAL',
    'Attendance.status': 'PERSONAL',
    'Payslip.net': 'FINANCIAL',
    'Payslip.gross': 'FINANCIAL',
    // Accounts and authentication.
    'User.name': 'PERSONAL',
    'User.email': 'PERSONAL',
    'User.phone': 'PERSONAL',
    'User.passwordHash': 'CREDENTIAL',
    'User.totpSecret': 'AUTHENTICATION_SECRET',
    'RecoveryCode.codeHash': 'CREDENTIAL',
    'Session.refreshTokenHash': 'CREDENTIAL',
    'Session.ip': 'PERSONAL',
    'Session.userAgent': 'PERSONAL',
    'PasswordResetToken.tokenHash': 'CREDENTIAL',
    'EmailOtp.codeHash': 'CREDENTIAL',
    'EmailOtp.email': 'PERSONAL',
    'SyncDevice.deviceSecretHash': 'CREDENTIAL',
    'PaymentSetting.keySecret': 'AUTHENTICATION_SECRET',
    'AuditLog.ip': 'PERSONAL',
    'AuditLog.userAgent': 'PERSONAL',
    'ErrorLog.ip': 'PERSONAL',
    // SARTHI's own customers - the hotels and their owners.
    'Tenant.contactName': 'PERSONAL',
    'Tenant.contactEmail': 'PERSONAL',
    'Tenant.contactPhone': 'PERSONAL',
    'SubscriptionInvoice.buyerName': 'PERSONAL',
    'SubscriptionInvoice.buyerEmail': 'PERSONAL',
    'SubscriptionInvoice.buyerPhone': 'PERSONAL',
    'SubscriptionInvoice.buyerAddress': 'PERSONAL',
    'CouponRedemption.phone': 'PERSONAL',
    'CouponRedemption.ip': 'PERSONAL',
    'Supplier.phone': 'PERSONAL',
    'Supplier.email': 'PERSONAL',
    'Testimonial.authorName': 'PERSONAL',
    // The privacy registers hold personal data of their own.
    'DataPrincipalRequest.subjectLabel': 'PERSONAL',
    'DataPrincipalRequest.contact': 'PERSONAL',
    'DataDisclosureRequest.requestedData': 'PERSONAL',
    'CouponRedemption.userAgent': 'PERSONAL',
};
export const classificationOf = (modelField) => DATA_CLASSIFICATION[modelField] ?? 'INTERNAL';
/** Classes whose values must never be logged, exported in bulk, or shown unmasked by default. */
export const RESTRICTED_CLASSES = [
    'GOVERNMENT_IDENTIFIER',
    'AUTHENTICATION_SECRET',
    'CREDENTIAL',
    'FINANCIAL',
    'SENSITIVE_PERSONAL',
];
export const PRIVACY_PURPOSES = [
    'SERVICE_DELIVERY',
    'BOOKING',
    'CHECK_IN',
    'BILLING',
    'TAX_COMPLIANCE',
    'PAYROLL',
    'AUTHENTICATION',
    'SECURITY',
    'CUSTOMER_SUPPORT',
    'LEGAL_COMPLIANCE',
    'MARKETING',
];
/**
 * What each purpose is, in a hotelier's words. Its processing basis and
 * whether consent is required are NOT here: they live in the
 * privacy_purpose_settings table, start UNDECIDED, and are set once a legal
 * review settles them. Code never assumes them.
 */
export const PURPOSE_LABELS = {
    SERVICE_DELIVERY: { label: 'Providing the service', description: 'Serving the guest - the stay, the meal, the order.' },
    BOOKING: { label: 'Bookings', description: 'Taking and managing room bookings and table reservations.' },
    CHECK_IN: { label: 'Check-in', description: 'Registering a guest on arrival, including identity documents where collected.' },
    BILLING: { label: 'Billing', description: 'Issuing bills and receipts and taking payment.' },
    TAX_COMPLIANCE: { label: 'Tax records', description: 'GST invoices and returns.' },
    PAYROLL: { label: 'Payroll', description: 'Paying staff, attendance, statutory deductions.' },
    AUTHENTICATION: { label: 'Signing in', description: 'Staff accounts, passwords and two-step verification.' },
    SECURITY: { label: 'Security', description: 'Protecting accounts and detecting misuse.' },
    CUSTOMER_SUPPORT: { label: 'Support', description: 'Helping a guest or a hotel with a problem.' },
    LEGAL_COMPLIANCE: { label: 'Legal obligations', description: 'Records a law requires to be kept or produced.' },
    MARKETING: { label: 'Marketing', description: 'Offers and promotions sent to a guest. Not sent by SARTHI today.' },
};
export const PROCESSING_BASES = ['UNDECIDED', 'CONSENT', 'LEGITIMATE_USE', 'LEGAL_OBLIGATION'];
export const DATA_SUBJECT_TYPES = [
    'GUEST',
    'BOOKING_GUEST',
    'RESERVATION_GUEST',
    'ORDER_CUSTOMER',
    'EMPLOYEE',
    'USER',
];
export const CONSENT_STATUSES = ['GRANTED', 'WITHDRAWN', 'DECLINED'];
export const CONSENT_SOURCES = [
    'CHECK_IN',
    'ONLINE_BOOKING',
    'GUEST_MENU',
    'RESERVATION',
    'ADMIN',
    'IMPORT',
];
export const NOTICE_TYPES = [
    'PRIVACY_POLICY',
    'GUEST_PRIVACY_NOTICE',
    'TERMS',
    'COOKIE_NOTICE',
    'DPA',
    'REFUND_POLICY',
    'GRIEVANCE_PROCEDURE',
    'RETENTION_POLICY',
    'INCIDENT_RESPONSE_POLICY',
    'GOVERNMENT_DISCLOSURE_PROCEDURE',
    'SUPPORT_ACCESS_POLICY',
    'SUBPROCESSOR_REGISTER',
];
/** Notice types a hotel writes for its own guests; every other type is SARTHI's own. */
export const PROPERTY_NOTICE_TYPES = ['GUEST_PRIVACY_NOTICE'];
export const NOTICE_LABELS = {
    PRIVACY_POLICY: 'Privacy Policy',
    GUEST_PRIVACY_NOTICE: 'Guest privacy notice',
    TERMS: 'Terms and Conditions',
    COOKIE_NOTICE: 'Cookie and storage notice',
    DPA: 'Data Processing Agreement',
    REFUND_POLICY: 'Refund policy',
    GRIEVANCE_PROCEDURE: 'Grievance procedure',
    RETENTION_POLICY: 'Data retention policy',
    INCIDENT_RESPONSE_POLICY: 'Incident response policy',
    GOVERNMENT_DISCLOSURE_PROCEDURE: 'Government disclosure procedure',
    SUPPORT_ACCESS_POLICY: 'Support access policy',
    SUBPROCESSOR_REGISTER: 'Subprocessor register',
};
/**
 * Marks text that a person, not this codebase, has to supply. Seeded legal
 * documents carry these until business and legal review replace them; a
 * notice that still contains one cannot be published (see the notices
 * service).
 */
export const BUSINESS_INPUT_REQUIRED = '[BUSINESS INPUT REQUIRED]';
export const LEGAL_REVIEW_REQUIRED = '[LEGAL REVIEW REQUIRED]';
export const containsPlaceholder = (text) => text.includes(BUSINESS_INPUT_REQUIRED) || text.includes(LEGAL_REVIEW_REQUIRED);
/**
 * On a seeded retention policy whose period nobody has decided yet. Such a
 * policy cannot be enabled while its note still says so.
 */
export const BUSINESS_DECISION_MARKER = 'BUSINESS/LEGAL DECISION REQUIRED';
/** Plain names for retention entities, for the consoles. */
export const RETENTION_ENTITY_LABELS = {
    EMAIL_OTP: 'Email verification codes',
    PASSWORD_RESET_TOKEN: 'Password reset links',
    DEVICE_PAIRING_CODE: 'Device pairing codes',
    SESSION: 'Expired and revoked sign-in sessions',
    NOTIFICATION: 'Staff notifications',
    DEVICE_ERROR: 'Device error reports',
    DEVICE_UPDATE_ATTEMPT: 'Device update history',
    SYNC_OPERATION: 'Offline sync ledger',
    BILLING_WEBHOOK_EVENT: 'Payment webhook payloads',
    COUPON_REDEMPTION_METADATA: 'Coupon redemption IP and browser',
    CAPTCHA_CHALLENGE: 'CAPTCHA challenges',
    ERROR_LOG: 'Server error log',
    AUDIT_SECURITY: 'Audit log - sign-in and security',
    AUDIT_PRIVACY: 'Audit log - privacy, exports and disclosures',
    AUDIT_BUSINESS: 'Audit log - business changes',
    GUEST: 'Guest records',
    ROOM_BOOKING: 'Online room bookings',
    RESERVATION: 'Table reservations',
    ORDER_CUSTOMER: 'Customer details on online orders',
    EMPLOYEE: 'Former employee records',
    DATA_REQUEST_CONTACT: "Requesters' contact details on closed data requests",
    CLOSED_ACCOUNT: 'Closed accounts - final deletion of everything kept after closure',
};
/* ---------------- records kept under law ---------------- */
/**
 * Records that keep some personal data after an erasure or an account
 * closure, because another law may require them to be kept.
 *
 * Personal data contained in records that must be retained under applicable
 * law may be retained to the extent necessary for that legal obligation -
 * and no further. `basis` names the law that is the likely source of the
 * obligation; it is a candidate for legal confirmation, not a conclusion,
 * and no retention period is set here: both are BUSINESS/LEGAL DECISION
 * REQUIRED (docs/privacy/DPDP-BUSINESS-DECISIONS.md, RETENTION-MATRIX.md).
 */
export const RETAINED_RECORD_KINDS = [
    'TAX_INVOICE',
    'STAY_RECORD',
    'PAYMENT_RECORD',
    'PAYROLL_RECORD',
    'ATTENDANCE_RECORD',
    'PURCHASE_RECORD',
    'CONSENT_EVIDENCE',
    'DATA_REQUEST_RECORD',
    'AUDIT_RECORD',
];
const TO_CONFIRM = 'To confirm with legal/tax adviser';
export const RETAINED_RECORDS = {
    TAX_INVOICE: {
        label: 'Tax invoices',
        holds: 'The guest or customer name and GSTIN printed on the invoice',
        basis: 'Candidate: GST record-keeping (CGST Act, 2017, ss. 35-36). ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    STAY_RECORD: {
        label: 'Stays and folios',
        holds: 'Dates, room and charges; linked to the (erased) guest by id only',
        basis: 'Candidate: support for the tax invoice; hotel guest-register requirements. ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    PAYMENT_RECORD: {
        label: 'Payments',
        holds: 'Amount, method and gateway reference; payer name and email are cleared',
        basis: 'Candidate: accounting and tax record-keeping; payment reconciliation. ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    PAYROLL_RECORD: {
        label: 'Payslips and payroll runs',
        holds: "Pay figures under the former employee's code",
        basis: 'Candidate: wage, provident-fund and state labour record-keeping. ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    ATTENDANCE_RECORD: {
        label: 'Attendance',
        holds: "Days worked under the former employee's code",
        basis: 'Candidate: labour record-keeping (supports payroll). ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    PURCHASE_RECORD: {
        label: 'Purchase orders, receipts and expenses',
        holds: 'Supplier business details and amounts',
        basis: 'Candidate: GST and accounting record-keeping. ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    CONSENT_EVIDENCE: {
        label: 'Consent records',
        holds: 'That consent was given or withdrawn, when, and under which notice; the person by id only',
        basis: 'Candidate: showing consent was obtained and withdrawn (DPDP Act, 2023). ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    DATA_REQUEST_RECORD: {
        label: 'Data principal requests',
        holds: "Reference, type, dates and outcome; the requester's name and contact are cleared after a holding period",
        basis: 'Candidate: showing a request was received and handled (DPDP Act, 2023). ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
    AUDIT_RECORD: {
        label: 'Audit trail',
        holds: 'Who did what, by account id; no personal values',
        basis: 'Candidate: security safeguards and log-keeping under the DPDP Rules, 2025. ' + TO_CONFIRM,
        period: TO_CONFIRM,
    },
};
//# sourceMappingURL=privacy.js.map